// FireMissionLayer — Fire Ops map overlay.
//
// Manages two independent data sources for fire operations context:
//
//   fire-tfrs       Active FAA TFRs (red fill + hatch), 5-min cache.
//                   LayerId: 'fireMissionTfrs'
//
//   fire-incidents  Active large wildfire incident locations from NIFC WFIGS
//                   (orange dot scaled by acreage), 10-min cache.
//                   LayerId: 'fireMissionIncidents'
//
// Click handlers register on the MapLibre map and call onTfrClick /
// onIncidentClick props so the parent (MapPage) can open a detail popup.
//
// Both sources are fetched every REFRESH_MS when the layer is visible;
// aborted and torn down on toggle-off. The component returns null (no
// DOM output) — it's a pure map-side-effect component.

import { useEffect, useRef, useCallback } from 'react';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { getMapInstance } from './MapView';
import type maplibregl from 'maplibre-gl';
import { GeoJSONSource } from 'maplibre-gl';

// ---- Source / layer IDs ---------------------------------------------------
const TFR_SOURCE    = 'fire-mission-tfrs-source';
const TFR_FILL      = 'fire-mission-tfrs-fill';
const TFR_LINE      = 'fire-mission-tfrs-line';
const TFR_HATCH     = 'fire-mission-tfrs-hatch';

const INC_SOURCE    = 'fire-mission-incidents-source';
const INC_CIRCLE    = 'fire-mission-incidents-circle';
const INC_LABEL     = 'fire-mission-incidents-label';

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

// Refresh interval (ms).
const TFR_REFRESH_MS  = 5  * 60 * 1_000;
const INC_REFRESH_MS  = 10 * 60 * 1_000;

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------
async function fetchTfrs(signal: AbortSignal): Promise<GeoJSON.FeatureCollection> {
  const r = await fetch('/api/fire/tfrs', { signal, cache: 'no-store' });
  if (!r.ok) throw new Error(`TFR fetch ${r.status}`);
  return r.json() as Promise<GeoJSON.FeatureCollection>;
}

async function fetchIncidents(signal: AbortSignal): Promise<GeoJSON.FeatureCollection> {
  const r = await fetch('/api/fire/incidents', { signal });
  if (!r.ok) throw new Error(`Incidents fetch ${r.status}`);
  return r.json() as Promise<GeoJSON.FeatureCollection>;
}

// ---------------------------------------------------------------------------
// Map source helpers
// ---------------------------------------------------------------------------
function setGeoJSON(map: maplibregl.Map, sourceId: string, data: GeoJSON.FeatureCollection) {
  const src = map.getSource(sourceId) as GeoJSONSource | undefined;
  if (src) {
    src.setData(data);
  }
}

function addTfrSources(map: maplibregl.Map) {
  if (!map.getSource(TFR_SOURCE)) {
    map.addSource(TFR_SOURCE, { type: 'geojson', data: EMPTY_FC });

    // Semi-transparent red fill
    map.addLayer({
      id: TFR_FILL,
      type: 'fill',
      source: TFR_SOURCE,
      paint: {
        'fill-color': '#dc2626',
        'fill-opacity': 0.12,
      },
    });

    // Solid red stroke
    map.addLayer({
      id: TFR_LINE,
      type: 'line',
      source: TFR_SOURCE,
      paint: {
        'line-color': '#dc2626',
        'line-width': 1.5,
        'line-opacity': 0.85,
      },
    });

    // Diagonal hatch overlay using a line layer with dasharray on the same source
    // (approximation — for a true hatch we'd need a pattern image; this gives a
    // strong red border that reads clearly without a canvas texture).
    map.addLayer({
      id: TFR_HATCH,
      type: 'line',
      source: TFR_SOURCE,
      paint: {
        'line-color': '#dc2626',
        'line-width': 0.5,
        'line-opacity': 0.4,
        'line-dasharray': [4, 4],
      },
    });
  }
}

function removeTfrSources(map: maplibregl.Map) {
  for (const id of [TFR_HATCH, TFR_LINE, TFR_FILL]) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  if (map.getSource(TFR_SOURCE)) map.removeSource(TFR_SOURCE);
}

function addIncidentSources(map: maplibregl.Map) {
  if (!map.getSource(INC_SOURCE)) {
    map.addSource(INC_SOURCE, { type: 'geojson', data: EMPTY_FC });

    // Orange circles, scaled logarithmically by IncidentSize.
    map.addLayer({
      id: INC_CIRCLE,
      type: 'circle',
      source: INC_SOURCE,
      paint: {
        'circle-color': '#f97316',   // orange-500
        'circle-stroke-color': '#7c2d12',
        'circle-stroke-width': 1,
        'circle-opacity': 0.85,
        // Scale: 100 ac → 6 px, 1000 ac → 9 px, 10 000 ac → 13 px, 100 000+ → 18 px
        'circle-radius': [
          'interpolate', ['linear'],
          ['coalesce', ['get', 'IncidentSize'], 100],
          100,   6,
          1000,  9,
          10000, 13,
          100000, 18,
        ],
      },
    });

    // Name label at zoom ≥ 7
    map.addLayer({
      id: INC_LABEL,
      type: 'symbol',
      source: INC_SOURCE,
      minzoom: 7,
      layout: {
        'text-field': ['get', 'IncidentName'],
        'text-size': 10,
        'text-offset': [0, 1.4],
        'text-anchor': 'top',
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': '#fed7aa',      // orange-200
        'text-halo-color': '#000',
        'text-halo-width': 1.5,
      },
    });
  }
}

function removeIncidentSources(map: maplibregl.Map) {
  for (const id of [INC_LABEL, INC_CIRCLE]) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  if (map.getSource(INC_SOURCE)) map.removeSource(INC_SOURCE);
}

// ---------------------------------------------------------------------------
// Props + component
// ---------------------------------------------------------------------------
export interface TfrPopupData {
  notamId: string;
  altitudeRange: string;
  description: string;
  effectiveDates: string;
  tfrType: string;
  listUrl: string;
  lngLat: { lng: number; lat: number };
}

export interface IncidentPopupData {
  name: string;
  acreage: number;
  containmentPct: number | null;
  lastUpdate: string;
  inciwebUrl: string | null;
  state: string;
  lngLat: { lng: number; lat: number };
}

interface Props {
  onTfrClick?: (data: TfrPopupData) => void;
  onIncidentClick?: (data: IncidentPopupData) => void;
}

export function FireMissionLayer({ onTfrClick, onIncidentClick }: Props) {
  const showTfrs      = useLayerVisibilityStore((s) => s.layers.fireMissionTfrs);
  const showIncidents = useLayerVisibilityStore((s) => s.layers.fireMissionIncidents);

  const tfrAbortRef = useRef<AbortController | null>(null);
  const incAbortRef = useRef<AbortController | null>(null);

  const tfrTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const incTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ---- TFR effect ----------------------------------------------------------
  const loadTfrs = useCallback(async () => {
    tfrAbortRef.current?.abort();
    const ac = new AbortController();
    tfrAbortRef.current = ac;
    try {
      const fc = await fetchTfrs(ac.signal);
      if (!ac.signal.aborted) {
        const map = getMapInstance();
        if (map) setGeoJSON(map, TFR_SOURCE, fc);
      }
    } catch (err: unknown) {
      if ((err as { name?: string })?.name !== 'AbortError') {
        console.warn('[FireMissionLayer] TFR fetch failed', err);
      }
    }
  }, []);

  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;

    if (!showTfrs) {
      tfrAbortRef.current?.abort();
      if (tfrTimerRef.current != null) clearInterval(tfrTimerRef.current);
      tfrTimerRef.current = null;
      removeTfrSources(map);
      return;
    }

    addTfrSources(map);
    void loadTfrs();
    tfrTimerRef.current = setInterval(() => void loadTfrs(), TFR_REFRESH_MS);

    // Click handler for TFR fill
    const onTfrFillClick = (e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
      if (!e.features?.length || !onTfrClick) return;
      const props = e.features[0].properties as Record<string, unknown>;
      const notamKey = typeof props.NOTAM_KEY === 'string' ? props.NOTAM_KEY : '';
      const notamId = notamKey ? notamKey.split('-')[0] : 'UNKNOWN';
      const low = typeof props.LOW_ALTITUDE === 'number' ? props.LOW_ALTITUDE : 0;
      const hi  = typeof props.HI_ALTITUDE  === 'number' ? props.HI_ALTITUDE  : 99_999;
      const loStr = low === 0 ? 'SFC' : `${low.toLocaleString()} ft`;
      const hiStr = hi >= 99_000 ? 'unlimited' : `${hi.toLocaleString()} ft`;
      onTfrClick({
        notamId,
        altitudeRange: `${loStr} – ${hiStr} MSL`,
        description: typeof props.tfr_description === 'string' ? props.tfr_description : '',
        effectiveDates: typeof props.tfr_creation_date === 'string' ? props.tfr_creation_date : '',
        tfrType: typeof props.tfr_type === 'string' ? props.tfr_type : '',
        listUrl: typeof props.tfr_list_url === 'string' ? props.tfr_list_url : 'https://tfr.faa.gov/tfr3/',
        lngLat: e.lngLat,
      });
    };

    map.on('click', TFR_FILL, onTfrFillClick);
    const prevCursorStyle = map.getCanvas().style.cursor;
    const setCursorPointer = () => { map.getCanvas().style.cursor = 'pointer'; };
    const resetCursor = () => { map.getCanvas().style.cursor = prevCursorStyle; };
    map.on('mouseenter', TFR_FILL, setCursorPointer);
    map.on('mouseleave', TFR_FILL, resetCursor);

    return () => {
      tfrAbortRef.current?.abort();
      if (tfrTimerRef.current != null) clearInterval(tfrTimerRef.current);
      tfrTimerRef.current = null;
      map.off('click', TFR_FILL, onTfrFillClick);
      map.off('mouseenter', TFR_FILL, setCursorPointer);
      map.off('mouseleave', TFR_FILL, resetCursor);
      removeTfrSources(map);
    };
  }, [showTfrs, loadTfrs, onTfrClick]);

  // ---- Incidents effect ----------------------------------------------------
  const loadIncidents = useCallback(async () => {
    incAbortRef.current?.abort();
    const ac = new AbortController();
    incAbortRef.current = ac;
    try {
      const fc = await fetchIncidents(ac.signal);
      if (!ac.signal.aborted) {
        const map = getMapInstance();
        if (map) setGeoJSON(map, INC_SOURCE, fc);
      }
    } catch (err: unknown) {
      if ((err as { name?: string })?.name !== 'AbortError') {
        console.warn('[FireMissionLayer] Incidents fetch failed', err);
      }
    }
  }, []);

  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;

    if (!showIncidents) {
      incAbortRef.current?.abort();
      if (incTimerRef.current != null) clearInterval(incTimerRef.current);
      incTimerRef.current = null;
      removeIncidentSources(map);
      return;
    }

    addIncidentSources(map);
    void loadIncidents();
    incTimerRef.current = setInterval(() => void loadIncidents(), INC_REFRESH_MS);

    // Click handler for incident circles
    const onIncidentClick_ = (e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
      if (!e.features?.length || !onIncidentClick) return;
      const props = e.features[0].properties as Record<string, unknown>;

      const name     = typeof props.IncidentName === 'string' ? props.IncidentName : 'Unknown Fire';
      const acreage  = typeof props.IncidentSize === 'number'  ? props.IncidentSize  : 0;
      const pct      = typeof props.PercentContained === 'number' ? props.PercentContained : null;
      const state    = typeof props.POOState === 'string' ? props.POOState : '';
      const rawMod   = typeof props.ModifiedOnDateTime === 'string' || typeof props.ModifiedOnDateTime === 'number'
        ? String(props.ModifiedOnDateTime)
        : '';
      const lastUpdate = rawMod
        ? new Date(typeof props.ModifiedOnDateTime === 'number' ? props.ModifiedOnDateTime : rawMod)
            .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : 'Unknown';

      // InciWeb URL — NIFC stores it in ICS209ReportURL if it contains 'inciweb'
      const icsUrl = typeof props.ICS209ReportURL === 'string' ? props.ICS209ReportURL : null;
      const inciwebUrl = icsUrl && icsUrl.toLowerCase().includes('inciweb') ? icsUrl : null;

      onIncidentClick({
        name,
        acreage,
        containmentPct: pct,
        lastUpdate,
        inciwebUrl,
        state,
        lngLat: e.lngLat,
      });
    };

    map.on('click', INC_CIRCLE, onIncidentClick_);
    const setCursorPointer = () => { map.getCanvas().style.cursor = 'pointer'; };
    const resetCursor = () => { map.getCanvas().style.cursor = ''; };
    map.on('mouseenter', INC_CIRCLE, setCursorPointer);
    map.on('mouseleave', INC_CIRCLE, resetCursor);

    return () => {
      incAbortRef.current?.abort();
      if (incTimerRef.current != null) clearInterval(incTimerRef.current);
      incTimerRef.current = null;
      map.off('click', INC_CIRCLE, onIncidentClick_);
      map.off('mouseenter', INC_CIRCLE, setCursorPointer);
      map.off('mouseleave', INC_CIRCLE, resetCursor);
      removeIncidentSources(map);
    };
  }, [showIncidents, loadIncidents, onIncidentClick]);

  // Pure side-effect component — no DOM output.
  return null;
}

/**
 * Exported for use in the route-conflict check (PlanPanel fetches TFRs
 * independently — this keeps the data-fetching logic co-located and
 * avoids duplicating the URL).
 */
export const FIRE_TFRS_URL = '/api/fire/tfrs';
